// Core imports
import React, { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';

// Component Imports
import { Card, Col, Row } from 'antd';
import { DislikeFilled, LikeFilled } from '@ant-design/icons';
import UserFeedbackActivitiesFeedbackScore from './UserFeedbackActivitiesFeedbackScore';
import UserFeedbackAutomatedAnswersScore from './UserFeedbackAutomatedAnswersScore';
import UserFeedbackScoreSection from './UserFeedbackScoreSection';
import UserFeedbackPerResponseSection from './UserFeedbackPerResponseSection';
import UserFeedbackAnalysis from './UserFeedbackAnalysis';
import UserFeedbackLiveChatAgentSection from './UserFeedbackLiveChatAgentSection';

// Misc imports
import { ANALYTICS_SECTIONS } from '../../constants/main';
import { useFetchUserFeedbackAnalyticsQuery } from '../../store/analytics/analyticsApi';
import { isEmptyArray } from '../../utils/is/isEmptyArray';

const AnalyticsUserFeedbackTab = ({
  requestParams,
  setIsLoading = () => false
}) => {

  const { data: userFeedbackAnalytics, isFetching: isFetchingUserFeedbackAnalytics, isUninitialized } = useFetchUserFeedbackAnalyticsQuery({ ...requestParams?.[0] }, { skip: isEmptyArray(requestParams) });

  // Memoize loading state to prevent unnecessary re-renders
  const isLoading = useMemo(() =>
    isFetchingUserFeedbackAnalytics || isUninitialized,
  [isFetchingUserFeedbackAnalytics, isUninitialized]
  );

  useEffect(() => {
    setIsLoading(isLoading);

    return () => {
      setIsLoading(false);
    };
  }, [isLoading]);

  const renderUserFeedbackSection = () => {
    return (
      <Fragment>
        <Row gutter={[16, 16]} className="mb-5 hide-empty" id={`analytics-section_${ANALYTICS_SECTIONS.USER_FEEDBACK.key}`}>
          <Col xs={24} md={12} xxl={12} id={`analytics-section_${ANALYTICS_SECTIONS.USER_FEEDBACK.key}_${ANALYTICS_SECTIONS.USER_FEEDBACK.subSections.OVERALL_FEEDBACK_SCORE.key}`}>
            <Card className="h-100">
              <UserFeedbackScoreSection
                data={userFeedbackAnalytics?.feedback}
                isLoading={isLoading}
                sectionTitle={t('Agent Overall Feedback Score')}
              />
            </Card>
          </Col>

          <Col xs={24} md={12} xxl={12} id={`analytics-section_${ANALYTICS_SECTIONS.USER_FEEDBACK.key}_${ANALYTICS_SECTIONS.USER_FEEDBACK.subSections.AUTOMATED_ANSWERS_FEEDBACK_SCORE.key}`}>
            <Card className="h-100">
              <UserFeedbackAutomatedAnswersScore
                data={userFeedbackAnalytics?.automatedAnswers}
                sectionTitle={t('Automated Answers Feedback Score')}
                isLoading={isLoading}
              />
            </Card>
          </Col>

          <Col xs={24} md={12} xxl={12} id={`analytics-section_${ANALYTICS_SECTIONS.USER_FEEDBACK.key}_${ANALYTICS_SECTIONS.USER_FEEDBACK.subSections.ACTIVITIES_FEEDBACK_SCORE.key}`}>
            <Card className="h-100">
              <UserFeedbackActivitiesFeedbackScore
                data={userFeedbackAnalytics?.activities}
                isLoading={isLoading}
                sectionTitle={t('Flows Feedback Score')}
              />
            </Card>
          </Col>

          <Col xs={24} md={12} xxl={12} id={`analytics-section_${ANALYTICS_SECTIONS.USER_FEEDBACK.key}_${ANALYTICS_SECTIONS.USER_FEEDBACK.subSections.PER_RESPONSE.key}`}>
            <Card className="h-100">
              <UserFeedbackPerResponseSection
                data={userFeedbackAnalytics?.botResponses}
                sectionTitle={t('Per Response')}
                isLoading={isLoading}
              />
            </Card>
          </Col>

          <Col xs={24} md={24} xxl={24} id={`analytics-section_${ANALYTICS_SECTIONS.USER_FEEDBACK.key}_${ANALYTICS_SECTIONS.USER_FEEDBACK.subSections.NEGATIVE_FEEDBACK_ANALYSIS.key}`}>
            <Card className="h-100">
              <UserFeedbackAnalysis
                data={userFeedbackAnalytics?.negativeFeedback}
                isLoading={isLoading}
                sectionTitle={t('Negative Feedback Analysis')}
                icon={<DislikeFilled className="ml-2 text-error" />}
              />
            </Card>
          </Col>

          <Col xs={24} md={24} xxl={24} id={`analytics-section_${ANALYTICS_SECTIONS.USER_FEEDBACK.key}_${ANALYTICS_SECTIONS.USER_FEEDBACK.subSections.POSITIVE_FEEDBACK_ANALYSIS.key}`}>
            <Card className="h-100">
              <UserFeedbackAnalysis
                data={userFeedbackAnalytics?.positiveFeedback}
                isLoading={isLoading}
                sectionTitle={t('Positive Feedback Analysis')}
                icon={<LikeFilled className="ml-2 text-success" />}
              />
            </Card>
          </Col>

          <Col xs={24} md={12} xxl={12} id={`analytics-section_${ANALYTICS_SECTIONS.USER_FEEDBACK.key}_${ANALYTICS_SECTIONS.USER_FEEDBACK.subSections.LIVE_CHAT_FEEDBACK_SCORE.key}`}>
            <Card className="h-100">
              <UserFeedbackScoreSection
                data={userFeedbackAnalytics?.liveChatFeedback}
                isLoading={isLoading}
                sectionTitle={t('LiveChat Feedback Score')}
              />
            </Card>
          </Col>

          <Col xs={24} md={12} xxl={12} id={`analytics-section_${ANALYTICS_SECTIONS.USER_FEEDBACK.key}_${ANALYTICS_SECTIONS.USER_FEEDBACK.subSections.LIVE_CHAT_AGENTS_FEEDBACK_SCORE.key}`}>
            <Card className="h-100">
              <UserFeedbackLiveChatAgentSection
                data={userFeedbackAnalytics?.liveChatAgentsFeedback}
                sectionTitle={t('Live Agents Feedback Score')}
                isLoading={isLoading}
              />
            </Card>
          </Col>
        </Row>
      </Fragment>
    );
  };
  return (
    <Fragment>
      {renderUserFeedbackSection()}
    </Fragment>
  );
};

AnalyticsUserFeedbackTab.propTypes = {
  requestParams: PropTypes.array,
  setIsLoading: PropTypes.func
};

export default AnalyticsUserFeedbackTab;
